(() => {
  if (window.mcNavigation) { window.mcNavigation.init(document); return; }

  const initHeader = header => {
    if (header.dataset.mcNavigationReady) return;
    header.dataset.mcNavigationReady = 'true';
    const nav = header.querySelector('.mc-primary-nav');
    const services = header.querySelector('.mc-service-nav');
    const dialog = header.querySelector('[data-mc-mobile-menu]');
    const toggle = header.querySelector('[data-mc-menu-toggle]');
    const backdrop = header.querySelector('[data-mc-menu-backdrop]');
    const body = dialog?.querySelector('[data-mc-mobile-menu-body]');
    if (!nav || !dialog || !toggle || !body) return;

    const controller = new AbortController();
    const headerGroup = header.closest('[data-selector="header-group"]');
    const filterControls = document.querySelector('[data-selector="filter-controls"]');
    const measureStickyHeader = () => {
      if (!header.isConnected) return;
      if (headerGroup) document.documentElement.style.setProperty('--mc-sticky-header-height', `${Math.ceil(headerGroup.getBoundingClientRect().height)}px`);
      if (filterControls) document.documentElement.style.setProperty('--mc-filter-controls-height', `${Math.ceil(filterControls.getBoundingClientRect().height)}px`);
    };
    const sizeObserver = new ResizeObserver(measureStickyHeader);
    if (headerGroup) sizeObserver.observe(headerGroup);
    if (filterControls) sizeObserver.observe(filterControls);
    measureStickyHeader();
    const on = (target, name, action, options = {}) => target?.addEventListener(name, action, {...options, signal: controller.signal});
    const mobile = window.matchMedia('(max-width: 989px)');
    const navHome = document.createComment('Main navigation position');
    const servicesHome = document.createComment('Services navigation position');
    nav.before(navHome);
    services?.before(servicesHome);
    const roots = [...nav.querySelectorAll(':scope > details')];
    let oldRootOverflow;
    let oldBodyOverflow;
    let locked = false;

    const closeDescendants = details => details.querySelectorAll('details[open]').forEach(item => { item.open = false; });
    const syncBackdrop = () => {
      backdrop.hidden = mobile.matches || !roots.some(item => item.open);
    };
    const closeAll = () => {
      nav.querySelectorAll('details[open]').forEach(item => { item.open = false; });
      syncBackdrop();
    };
    const positionPanels = () => {
      if (mobile.matches) return;
      const category = nav.querySelector('.mc-category-menu > summary');
      if (category) {
        const rect = category.getBoundingClientRect();
        let left = Math.max(16, rect.left);
        let width = Math.min(285, Math.floor((document.documentElement.clientWidth - left - 16) / 3));
        if (width < 200) { left = 16; width = Math.floor((document.documentElement.clientWidth - 32) / 3); }
        header.style.setProperty('--mc-menu-left', `${left}px`);
        header.style.setProperty('--mc-menu-width', `${width}px`);
        header.style.setProperty('--mc-menu-top', `${Math.round(header.getBoundingClientRect().bottom + 4)}px`);
      }
      const brands = nav.querySelector('.mc-brands-menu > summary');
      if (brands) {
        const rect = brands.getBoundingClientRect();
        header.style.setProperty('--mc-brands-left', `${Math.max(16, Math.min(rect.left, document.documentElement.clientWidth - 276))}px`);
        header.style.setProperty('--mc-brands-top', `${Math.round(header.getBoundingClientRect().bottom + 4)}px`);
      }
    };
    const unlock = () => {
      if (!locked) return;
      document.documentElement.style.overflow = oldRootOverflow;
      document.body.style.overflow = oldBodyOverflow;
      locked = false;
    };
    const closeMobile = () => {
      if (dialog.open) dialog.close();
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-label', 'Open menu');
      closeAll();
      unlock();
    };
    const moveForViewport = () => {
      closeMobile();
      if (mobile.matches) {
        body.append(nav);
        if (services) body.append(services);
      } else {
        navHome.after(nav);
        if (services) servicesHome.after(services);
        positionPanels();
      }
    };

    on(toggle, 'click', () => {
      if (!mobile.matches) return;
      // Search uses the existing native search implementation; collapse its
      // optional header row before opening the menu.
      if (header.dataset.mcSearchOpen === 'true') header.querySelector('[data-mc-search-toggle]')?.click();
      closeAll();
      const category = nav.querySelector('.mc-category-menu');
      if (category) category.open = true;
      oldRootOverflow = document.documentElement.style.overflow;
      oldBodyOverflow = document.body.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      locked = true;
      dialog.showModal();
      toggle.setAttribute('aria-expanded', 'true');
      toggle.setAttribute('aria-label', 'Close menu');
      dialog.querySelector('[data-mc-menu-close]').focus();
    });
    on(dialog.querySelector('[data-mc-menu-close]'), 'click', closeMobile);
    on(dialog, 'close', () => {
      closeMobile();
      if (mobile.matches && header.isConnected) toggle.focus();
    });
    on(dialog, 'click', event => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) closeMobile();
    });
    on(dialog, 'keydown', event => {
      if (event.key !== 'Tab') return;
      const stops = [...dialog.querySelectorAll('a[href], button:not([disabled]), summary, input:not([disabled]), [tabindex]:not([tabindex="-1"])')].filter(item => {
        if (!item.getClientRects().length || getComputedStyle(item).visibility === 'hidden') return false;
        for (let parent = item.parentElement; parent && parent !== dialog; parent = parent.parentElement) {
          if (parent.tagName === 'DETAILS' && !parent.open && item !== parent.querySelector(':scope > summary')) return false;
        }
        return true;
      });
      const first = stops[0];
      const last = stops[stops.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    });

    nav.querySelectorAll('details').forEach(details => {
      const summary = details.querySelector(':scope > summary');
      summary?.setAttribute('aria-expanded', String(details.open));
      on(details, 'toggle', () => {
        summary?.setAttribute('aria-expanded', String(details.open));
        if (details.open) {
          const peers = details.parentElement.tagName === 'LI'
            ? details.parentElement.parentElement.querySelectorAll(':scope > li > details')
            : roots;
          [...peers].forEach(other => { if (other !== details) { other.open = false; closeDescendants(other); } });
          positionPanels();
        } else closeDescendants(details);
        syncBackdrop();
      });
    });
    on(nav, 'keydown', event => {
      const summary = event.target.closest('summary');
      if (!summary) return;
      const details = summary.parentElement;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        details.open = true;
        details.querySelector(':scope > ul > li > a, :scope > ul > li > details > summary, :scope > div a')?.focus();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        if (details.open) { details.open = false; closeDescendants(details); }
        else details.parentElement.closest('details')?.querySelector('summary')?.focus();
      }
    });
    on(document, 'keydown', event => {
      if (mobile.matches || event.key !== 'Escape' || !header.isConnected) return;
      const opened = event.target.closest?.('details[open]') || roots.find(item => item.open);
      if (!opened || !nav.contains(opened)) return;
      event.preventDefault();
      opened.open = false;
      closeDescendants(opened);
      opened.querySelector('summary').focus();
      syncBackdrop();
    });
    // Consume dismissal clicks before links can navigate. A click on another
    // top-level menu still switches menus with one click.
    on(document, 'click', event => {
      if (mobile.matches || !header.isConnected || !roots.some(item => item.open)) return;
      const target = event.target;
      const owner = target.closest?.('.mc-primary-nav > details');
      if (owner && nav.contains(owner)) return;
      event.preventDefault();
      event.stopPropagation();
      closeAll();
    }, {capture: true});
    on(nav, 'click', event => {
      if (event.target.closest('a')) {
        if (mobile.matches) closeMobile();
        else closeAll();
      }
    });
    on(window, 'resize', () => { measureStickyHeader(); positionPanels(); });
    on(window, 'scroll', positionPanels, {passive: true});
    on(mobile, 'change', moveForViewport);
    on(document, 'shopify:section:unload', event => {
      if (!event.target.contains(header)) return;
      closeMobile();
      sizeObserver.disconnect();
      controller.abort();
    });
    moveForViewport();
  };

  // Gorgias supplies one launcher. Its three gradient decoration layers can
  // look like a second bubble. Keep the native button and conversation while
  // giving that button one clean surface in this theme only.
  const initChatSurface = () => {
    const seen = new WeakSet();
    const styleFrame = frame => {
      try {
        const doc = frame.contentDocument;
        if (!doc?.head || doc.getElementById('mc-chat-surface')) return;
        const style = doc.createElement('style');
        style.id = 'mc-chat-surface';
        style.textContent = `
          #gorgias-chat-messenger-button { background: #c51c2d !important; color: white !important; border: 0 !important; box-shadow: 0 2px 8px #0003 !important; }
          #gorgias-chat-messenger-button > div:empty { display: none !important; }
          #gorgias-chat-messenger-button > div { background: none !important; }
          #gorgias-chat-messenger-button svg path { fill: white !important; }
          #gorgias-chat-messenger-button svg ellipse { fill: #c51c2d !important; }
          #gorgias-chat-messenger-button:focus-visible { outline: 3px solid #087ba8 !important; outline-offset: 2px; }
        `;
        doc.head.append(style);
      } catch { /* Keep the native chat working if the vendor changes origin. */ }
    };
    const check = () => {
      const frame = document.querySelector('#gorgias-chat-container iframe#chat-button');
      if (!frame) return;
      if (!seen.has(frame)) { seen.add(frame); frame.addEventListener('load', () => styleFrame(frame)); }
      styleFrame(frame);
    };
    const watch = new MutationObserver(() => {
      const container = document.getElementById('gorgias-chat-container');
      if (!container) return;
      watch.disconnect();
      new MutationObserver(check).observe(container, {childList: true, subtree: true});
      check();
    });
    const container = document.getElementById('gorgias-chat-container');
    if (container) { new MutationObserver(check).observe(container, {childList: true, subtree: true}); check(); }
    else watch.observe(document.body, {childList: true, subtree: true});
  };
  const init = scope => scope.querySelectorAll('[data-mc-header]').forEach(initHeader);
  window.mcNavigation = {init};
  init(document);
  initChatSurface();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
